import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { ExerciseCard } from "../ExerciseCard";
import { getexercise } from "../funtionsBD/Mainservice";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useBrand } from "../../context/BrandContext";
import { normalizeAppLang } from "../../utils/appLanguages";
import { localizedExerciseName, localizedField } from "../../utils/localizedCatalogFields";
import "../PlayerSessionResultsDialog.css";
import "./ExchangeExerciseDialog.css";

function exerciseName(ex, lng) {
  return localizedExerciseName(ex, normalizeAppLang(lng)) || ex?.ex_id || "";
}

/**
 * Catalog browser filtered to the same subtype or competency block.
 * Coach picks a catalog exercise to switch onto the current training slot.
 */
export function ExchangeExerciseDialog({
  open,
  onClose,
  onSelect,
  typeId = null,
  subTypeId = null,
  cbId = null,
  groupId = null,
  currentExId = null,
  filterLabel = "",
  busy = false,
  initialQuery = "",
}) {
  const { t, i18n } = useTranslation();
  const { themeMode } = useBrand();
  const themeClass = themeMode === "light" ? "light-mode" : "dark-mode";
  const lng = normalizeAppLang(i18n.language);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) {
      setList([]);
      setQuery("");
      setError("");
      setSelectedId(null);
      return undefined;
    }
    setQuery(String(initialQuery || "").trim());
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = {
          type_id: typeId || undefined,
          sub_type_id: subTypeId || undefined,
          cb_id: cbId || undefined,
          group_id: groupId || undefined,
        };
        const response = await getexercise(params);
        if (cancelled) return;
        const rows = (response?.data?.data || []).map((ex) => ({
          ...ex,
          duration: ex.duration ?? 10,
        }));
        setList(rows);
        if (rows.length === 0) {
          setError(
            t("noCatalogExercisesFound") ||
              "No catalog exercises found for this skill or block."
          );
        }
      } catch (err) {
        if (!cancelled) {
          setList([]);
          setError(
            err?.message ||
              t("errorLoadingExercises") ||
              "Could not load catalog exercises."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, typeId, subTypeId, cbId, groupId, initialQuery, t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((ex) => {
      const name = exerciseName(ex, lng).toLowerCase();
      const objective = localizedField(ex, "ex_objective", lng).toLowerCase();
      return name.includes(q) || objective.includes(q);
    });
  }, [list, query, lng]);

  const handlePick = (ex) => {
    if (!ex?.ex_id || busy) return;
    if (String(ex.ex_id) === String(currentExId)) {
      onClose?.();
      return;
    }
    setSelectedId(ex.ex_id);
    onSelect?.(ex);
  };

  if (!open) return null;

  return createPortal(
    <div
      className={`player-session-results__backdrop exchange-ex__backdrop ${themeClass}`}
      onClick={busy ? undefined : onClose}
      role="presentation"
    >
      <div
        className={`player-session-results__sheet exchange-ex__sheet ${themeClass}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("exchangeExercise") || "Exchange exercise"}
      >
        <div className="player-session-results__handle" />
        <button
          type="button"
          className="player-session-results__close"
          onClick={onClose}
          disabled={busy}
          aria-label={t("close") || "Close"}
        >
          <CloseIcon />
        </button>

        <div className="player-session-results__content">
          <div className="player-session-results__header">
            <button
              type="button"
              className="player-session-results__back-btn player-session-results__back-btn--ios-nav"
              onClick={onClose}
              disabled={busy}
            >
              <ArrowBackIcon fontSize="small" />
              <span>{t("back") || "Back"}</span>
            </button>
            <div className="player-session-results__header-new">
              <div className="player-session-results__header-text">
                <h2 className="player-session-results__title-new">
                  <SwapHorizIcon sx={{ fontSize: 22, verticalAlign: "-4px", mr: 0.75 }} />
                  {t("exchangeExercise") || "Exchange exercise"}
                </h2>
                {filterLabel ? (
                  <p className="player-session-results__subtitle-new">
                    {filterLabel}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="exchange-ex__toolbar">
            <label className="exchange-ex__search">
              <SearchIcon sx={{ fontSize: 18, color: "var(--text-secondary, #78909c)" }} />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={
                  t("searchExercisesByNameOrDescription") ||
                  "Search by name or description…"
                }
                disabled={busy || loading}
              />
            </label>
            <p className="exchange-ex__hint">
              {t("exchangeExerciseHint") ||
                "Same skill or competency block — tap a drill to switch."}
            </p>
          </div>

          <div className="player-session-results__body exchange-ex__body">
            {loading ? (
              <p className="exchange-ex__status">{t("loading") || "Loading…"}</p>
            ) : error && filtered.length === 0 ? (
              <p className="exchange-ex__status exchange-ex__status--error">{error}</p>
            ) : filtered.length === 0 ? (
              <p className="exchange-ex__status">
                {t("noCatalogExercisesFound") || "No matching exercises."}
              </p>
            ) : (
              <div className="exchange-ex__grid">
                {filtered.map((ex) => {
                  const isCurrent = String(ex.ex_id) === String(currentExId);
                  const isSelected = String(ex.ex_id) === String(selectedId);
                  return (
                    <div
                      key={ex.ex_id}
                      className={`exchange-ex__card-wrap${
                        isCurrent ? " exchange-ex__card-wrap--current" : ""
                      }${isSelected ? " exchange-ex__card-wrap--selected" : ""}`}
                    >
                      {isCurrent ? (
                        <span className="exchange-ex__current-badge">
                          {t("currentExercise") || "Current"}
                        </span>
                      ) : null}
                      <ExerciseCard
                        exercise={ex}
                        isSelected={isSelected || isCurrent}
                        onSelect={() => handlePick(ex)}
                        lng={lng}
                        t={t}
                        selectMode="single"
                        showDurationInput={false}
                        slotCbName={filterLabel || null}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {busy ? (
              <p className="exchange-ex__busy">
                {t("exchangingExercise") || "Switching exercise…"}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ExchangeExerciseDialog;
